import { makeAutoObservable, runInAction, observable, when } from 'mobx';
import { getAccessToken, getPresignedUrl, apiFetcher } from '../utils/fetch';
import config from '../config';
import { stripRichFormatting } from '../utils/stringProcessor';
import { BAILEY_PERMISSION } from '../constants/permission';

const API_URL = process.env.NEXT_PUBLIC_GATEWAY_URL ? (`${process.env.NEXT_PUBLIC_GATEWAY_URL}/whatsapp`) : (config.GATEWAY_URL ? (`${config.GATEWAY_URL}/whatsapp`) : 'http://localhost:8080/api/v2/whatsapp');
const WS_URL = process.env.NEXT_PUBLIC_DOMAIN_URL ? process.env.NEXT_PUBLIC_DOMAIN_URL : (config.DOMAIN_URL || 'https://apidev.superkos.id');
const SOCKET_TIMEOUT = 8000;
const FETCH_DELAY = 600;
const PRESIGN_BATCH_SIZE = 5;
const MESSAGE_CLEANUP_INTERVAL = 300000; // 5 minutes

const BROADCAST_STORAGE_PREFIX = 'broadcast_';
const BROADCAST_LIST_STORAGE_PREFIX = 'broadcastLists_';
const BROADCAST_MESSAGE_STORAGE_PREFIX = 'broadcastMessages_';
const BROADCAST_CLEANUP_DAYS = 30; // Clean up broadcasts older than 30 days

const MESSAGE_STATUS = {
  '0': 0, 'error': 0, 'failed': 0, 'fail': 0,
  '1': 1, 'pending': 1, 'sending': 1,
  '2': 2, 'sent': 2, 'server_ack': 2, 'serverack': 2,
  '3': 3, 'delivered': 3, 'delivery_ack': 3, 'deliveryack': 3, 'receive': 3, 'received': 3,
  '4': 4, 'read': 4, 'seen': 4, 'viewed': 4,
  '5': 5, 'played': 5
};

export default class WhatsappStore {
  // Core state
  root = null;
  
  // Connection management
  _socketsByProperty = new Map(); // propertyId -> socket
  _connectionStates = new Map(); // propertyId -> connection state
  _subscriptionStates = new Map(); // propertyId -> subscription state
  
  // Session management
  sessionsByProperty = observable.map(); // propertyId -> session info
  
  // Data storage
  chatsByPropertyId = observable.map(); // propertyId -> chat list
  messagesByChatId = observable.map(); // ${chatId}-${propertyId} -> message list
  tenantSnapshotByProperty = observable.map();
  
  // Request management
  _activeRequests = new Map(); // key -> Promise
  _messagesFetchedForChat = new Set(); // Set of fetched chat keys
  _fetchTimers = new Map(); // propertyId -> timer
  _refreshingTenants = new Set();

  // Broadcast lists
  broadcastLists = observable.map(); // propertyId -> broadcast lists
  _broadcastTimers = new Map(); // propertyId -> cleanup timers
  
  // Sync status
  historySync = observable({
    syncing: false,
    status: null,
    phoneNumber: null,
    propertyId: null
  });

  usingFallback = false;

  setUsingFallback(value) {
    try {
      this.usingFallback = Boolean(value);
    } catch (err) {
      // ignore
    }
  }

  userHasBaileyPermission = false;

  constructor(root) {
    this.root = root;
    makeAutoObservable(this, {
      _socketsByProperty: false,
      _connectionStates: false,
      _subscriptionStates: false,
      _activeRequests: false,
      _fetchTimers: false,
      _refreshingTenants: false,
      _broadcastTimers: false,
      _messagesFetchedForChat: false,
    });

    // Auto-cleanup old messages periodically
    this._cleanupInterval = setInterval(() => {
      this._cleanupOldMessages();
    }, MESSAGE_CLEANUP_INTERVAL);
    // Auto-start sockets is handled explicitly via initWhenReady()

  }

  // ===== Permission Methods =====
  hasPermission(permission) {
    if (!this.root?.rbac) {
      return false;
    }
    
    // ===== FIX: Check if permissions are empty and try to load them =====
    const permissions = this.root.rbac.permissions;
    
    // If permissions are empty, assume no permission (safer approach)
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      return false;
    }
    
    return this.root.rbac.hasPermission?.(permission) || false;
  }

  arePermissionsLoaded() {
    if (!this.root?.rbac) return false;
    
    const permissions = this.root.rbac.permissions;
    return permissions && Array.isArray(permissions) && permissions.length > 0;
  }

  async hasBaileyPermission() {
    const timeout = 10000; // 10 seconds
    await Promise.race([
      when(() => this.arePermissionsLoaded()),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout waiting for permissions')), timeout))
    ]);
    return this.hasPermission(BAILEY_PERMISSION);
  }

  // Public initializer: wait for permissions then connect sockets for eligible properties
  initWhenReady() {
    // Use mobx when to wait until permissions are loaded, then run the startup logic
    when(() => this.arePermissionsLoaded()).then(async () => {
      try {
        const allowed = await this.hasBaileyPermission();
        if (allowed) {
          runInAction(() => {
            this.userHasBaileyPermission = true;
          });

          const propertyIds = this.root?.property?.items || [];

          this.refreshTenantsForAllProperties();
          
          // Sequentially ensure sockets and fetch chats so nav counts are populated
          await Promise.allSettled(
            propertyIds
              .filter(property => this.shouldUseSocket(property._id))
              .map(async (property) => {
                try {
                  if (property.phone && !this._subscriptionStates.get(property._id)) {
                    await this.subscribeSocket(property._id, property.phone, false);
                  }
                } catch (err) {
                  console.warn(`Failed to subscribe socket for property ${property._id}:`, err);
                }

                try {
                  await this.fetchChats(property._id, false);
                } catch (err) {
                  console.warn(`Failed to fetch chats for property ${property._id}:`, err);
                }
              })
          );

        }
      } catch (err) {
        // If permission check fails or times out, skip startup. Permission may arrive later and init can be retried.
        // console.warn('Failed to verify bailey permission during initialization:', err);
      }
    });
  }

  // ===== Utility Methods =====
  normalizePhone = (phone) => String(phone || '').replace(/[^\d]/g, '');

  getChatId(key) {
    if (!key) return null;
    return key.fromMe ? key.remoteJid : key.remoteJidAlt || key.remoteJid;
  }
  
  cleanJid(jid) {
    if (typeof jid !== 'string') return jid;
    return jid.replace(/^([^:@]+):[^@]+(@.+)$/, '$1$2');
  }

  extractPhoneFromJid(jid) {
    return this.normalizePhone(this.cleanJid(jid).split('@')[0]);
  }

  shouldUseSocket(propertyId, isSuperkos = false) {
    return !isSuperkos && propertyId && typeof propertyId === 'string';
  }

  _generateChatKey(chatId, propertyId, isSuperkos = false) {
    return isSuperkos ? `${chatId}-${propertyId}-superkos` : `${chatId}-${propertyId}`;
  }

  _generatePropertyKey(propertyId, isSuperkos = false) {
    return isSuperkos ? `${propertyId}-superkos` : propertyId;
  }

  _isPresignedUrl(url) {
    if (!url) return false;
    
    // Check if it's already a presigned URL (contains AWS signature parameters)
    return url.includes('X-Amz-Signature') || 
          url.includes('AWSAccessKeyId') || 
          url.includes('x-amz-credential') ||
          url.startsWith('blob:') || // Local blob URLs are ready to use
          url.startsWith('data:');   // Data URLs are ready to use
  }

  _extractNameFromMessage(message) {
    return message.pushName || 
          message.sender?.split('@')[0] || 
          message.key?.remoteJid?.split('@')[0] ||
          null;
  }

  // Prevent status downgrades
  _isStatusDowngrade = (currentStatus, newStatus) => {
    const current = typeof currentStatus === 'string' ? MESSAGE_STATUS[currentStatus.toLowerCase()] : currentStatus;
    const incoming = typeof newStatus === 'string' ? MESSAGE_STATUS[newStatus.toLowerCase()] : newStatus;

    // Allow error status (0) to override any status
    if (incoming === 0) return false;

    // Prevent downgrades (higher status to lower status)
    return current > incoming;
  };

  // ===== Cleanup Methods =====
  _cleanupOldMessages() {
    const now = Date.now();
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

    this.messagesByChatId.forEach((entry, key) => {
      if (!entry?.items?.length) return;
      
      const oldCount = entry.items.length;
      entry.items.replace(
        entry.items.filter(msg => {
          const messageAge = now - new Date(msg.timestamp).getTime();
          return messageAge < MAX_AGE || msg.isOptimistic;
        })
      );
    });
  }

  dispose() {
    // Clean up all resources
    clearInterval(this._cleanupInterval);
    
    this._fetchTimers.forEach(timer => clearTimeout(timer));
    this._fetchTimers.clear();
    
    this._socketsByProperty.forEach(socket => {
      try {
        socket.disconnect();
      } catch (e) {
        // console.warn('Socket disconnect error:', e);
      }
    });
    this._socketsByProperty.clear();
    
    // Clean up blob URLs
    this.messagesByChatId.forEach(entry => {
      entry.items?.forEach(msg => {
        if (msg.content?.isLocalBlob && msg.content?.presignUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(msg.content.presignUrl);
        }
      });
    });

    // Clear broadcast timers
    this._broadcastTimers.forEach(timer => clearTimeout(timer));
    this._broadcastTimers.clear();
  }

  // ===== Session Management =====
  setSession(propertyId, updates) {
    runInAction(() => {
      const current = this.sessionsByProperty.get(propertyId) || {};
      this.sessionsByProperty.set(propertyId, { ...current, ...updates });
    });
  }

  getSession(propertyId) {
    return this.sessionsByProperty.get(propertyId) || {};
  }

  stopSession(propertyId) {
    if (!propertyId) {
      // console.warn('stopSession called without propertyId');
      return;
    }
    
    runInAction(() => {
      // Clear session data
      this.sessionsByProperty.delete(propertyId);
      
      // Update connection state
      this._connectionStates.set(propertyId, 'disconnected');
      this._subscriptionStates.set(propertyId, false);
    });

    // Perform logout cleanup
    this._logoutSession(propertyId);
  }

  async _logoutSession(propertyId) {
    if (!propertyId) return;

    // Get socket reference outside runInAction
    const socket = this._socketsByProperty.get(propertyId);
    
    // Unsubscribe and remove socket
    if (socket) {
      try {
        // Emit unsubscribe event before disconnecting
        if (socket.connected) {
          socket.emit('unsubscribe', { propertyId });
        }
        
        // Disconnect socket
        socket.disconnect();
      } catch (error) {
        // console.error(`❌ Socket disconnect error for ${propertyId}:`, error);
      }
      
      // Remove from tracking
      runInAction(() => {
        this._socketsByProperty.delete(propertyId);
        this._connectionStates.set(propertyId, 'disconnected');
        this._subscriptionStates.set(propertyId, false);
      });
    }

    // Notify backend about disconnection
    try {
      await apiFetcher().post(`${API_URL}/session/disconnect`, { 
        propertyId: String(propertyId) 
      });
    } catch (error) {
      // console.error(`❌ API logout error for ${propertyId}:`, error);
      // Don't throw - this is cleanup, should be non-blocking
    }
  }

  _fetchSessionStatus(socket, phoneNumber) {
    return new Promise((resolve, reject) => {
      socket.emit('session_status', { phoneNumber }, (response) => {
        if (response.error) {
          reject(response.error);
        } else {
          resolve(response.data);
        }
      });
    });
  }

  // ===== Socket Management =====
  async ensureSocket(propertyId, isSuperkos = false) {
    if (isSuperkos) {
      return null;
    }

    if (!this.userHasBaileyPermission) {
      return null;
    }

    if (this._socketsByProperty.has(propertyId)) {
      const socket = this._socketsByProperty.get(propertyId);
      if (socket.connected) return socket;
    }

    const { io } = await import('socket.io-client');
    
    const socket = io(WS_URL, {
      // path: '/ws',
      transports: ['websocket'],
      withCredentials: true,
      auth: { token: `Bearer ${getAccessToken()}` },
      timeout: SOCKET_TIMEOUT
    });

    socket.propertyId = propertyId;
    this._socketsByProperty.set(propertyId, socket);
    
    await this._setupSocketListeners(socket, propertyId);
    await this._waitForConnection(socket);
    
    return socket;
  }

  async _waitForConnection(socket) {
    if (socket.connected) return;
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Socket connection timeout for ${socket.propertyId}`));
      }, SOCKET_TIMEOUT);

      const cleanup = () => {
        clearTimeout(timeout);
        socket.off('connect', onConnect);
        socket.off('connect_error', onError);
      };

      const onConnect = () => {
        cleanup();
        resolve();
      };

      const onError = (error) => {
        cleanup();
        reject(error);
      };

      socket.once('connect', onConnect);
      socket.once('connect_error', onError);
    });
  }

  async _setupSocketListeners(socket, propertyId) {
    if (socket._listenersSetup) return;
    
    // Connection events
    socket.on('connect', () => {
      this._connectionStates.set(propertyId, 'connected');

      // ===== AUTO-RESUBSCRIBE ON RECONNECTION =====
      if (this._subscriptionStates.get(propertyId)) {
        // Try to get phone number from session first
        const session = this.getSession(propertyId);
        let phoneNumber = session?.phoneNumber;
        
        // Fallback to property phone if not in session
        if (!phoneNumber) {
          const property = this.root?.property?.items?.find(p => p._id === propertyId);
          phoneNumber = property?.phone;
        }
        
        if (phoneNumber) {
          // console.log(`🔄 Auto-resubscribing to ${propertyId} with phone ${phoneNumber}`);
          socket.emit('subscribe', { 
            phoneNumber: String(phoneNumber), 
            propertyId: String(propertyId)
          });
        } else {
          // console.warn(`⚠️ Cannot auto-resubscribe to ${propertyId}: no phone number available`);
          this._subscriptionStates.set(propertyId, false);
        }
      }
    });

    socket.on('disconnect', (reason) => {
      // console.log(`🔌 Socket disconnected for ${propertyId}, reason:`, reason);
      this._connectionStates.set(propertyId, 'disconnected');
      
      // Clear session status on disconnect to ensure fresh state on reconnect
      this.setSession(propertyId, { 
        status: 'disconnected',
        qrCode: null,
        user: null
      });
    });

    socket.io.on('reconnect', (attemptNumber) => {
      // console.log(`🔄 Socket reconnected for property ${propertyId} after ${attemptNumber} attempts`);
      // The 'connect' event will handle resubscription
    });

    // Session events
    socket.on('subscribed', async (payload) => {
      if (payload.success && !payload.isConnected) {
        this.setSession(propertyId, { status: 'subscribed' });
        return;
      }
      this.setSession(propertyId, { connectedPhoneNumber: payload.phoneNumber });

      const phoneNumber = this.extractPhoneFromJid(payload.phoneNumber);
      this._subscriptionStates.set(propertyId, true);
      
      try {
        await this._handleSessionStatus(propertyId, phoneNumber);
        // Fetch chats after subscription/session status resolved so UI counts are up-to-date
        try {
          await this.fetchChats(propertyId, false);
        } catch (err) {
          // Non-fatal: log and continue
          // console.warn(`Failed to fetch chats after subscribed for ${propertyId}:`, err);
        }
      } catch (error) {
        // console.error(`❌ Session status handling failed for ${propertyId}:`, error);
        this.setSession(propertyId, { status: 'ERROR' });
      }
    });

    socket.on('qr_code', (payload) => {
      // console.log(`📱 QR Code received for ${propertyId}`, payload.qrCode ? 'QR Data Present' : 'No QR Data');
      this.setSession(propertyId, { qrCode: payload.qrCode });
    });

    socket.on('connection_status', (payload) => {
      this.setSession(propertyId, {
        status: payload.status,
        user: payload.user,
        connectedPhoneNumber: payload.phoneNumber || this.getSession(propertyId)?.connectedPhoneNumber || null,
        // Clear QR code when connected, keep it null for other statuses unless explicitly set by qr_code event
        qrCode: payload.status === 'connected' ? null : undefined
      });

      // When the socket reports 'connected', refresh chats so nav badge reflects server state
      if (payload.status === 'connected') {
        (async () => {
          try {
            await this.fetchChats(propertyId, false);
          } catch (err) {
            // Non-fatal
            // console.warn(`Failed to fetch chats after connection_status connected for ${propertyId}:`, err);
          }
        })();
      }
    });

    socket.on('connection_conflict', (payload) => {
      runInAction(() => {
        this.setSession(propertyId, { 
          status: 'conflict', 
          user: payload.user,
          conflictReason: payload.reason || 'Multiple sessions detected',
          conflictTimestamp: new Date().toISOString()
        });
      });

      // Show toast notification as well
      if (this.root?.pushToastMessage) {
        this.root.pushToastMessage({
          message: 'WhatsApp connection conflict detected. Please close other WhatsApp sessions.',
          severity: 'warning',
          autoHideDuration: 8000
        });
      }
    });

    socket.on('history-sync', (payload) => {
      runInAction(() => {
        Object.assign(this.historySync, payload);
      });
    });

    // Message events
    socket.on('message_received', (msg) => {
      this._handleMessageReceived(msg);
    });
    
    // Message status change event
    socket.on('message_status', (updates) => {
      this._handleMessageStatusUpdate(updates);
    });

    socket.on('error', (error) => {
      // console.error(`[${propertyId}] Socket error:`, error);
    });

    socket._listenersSetup = true;
  }

  async _handleSessionStatus(propertyId, phoneNumber) {
    const socket = this._socketsByProperty.get(propertyId);
    if (!socket) throw new Error('Socket not available for session status handling');
    // Fetch initial session status
    const status = await this._fetchSessionStatus(socket, phoneNumber);
    this.setSession(propertyId, status);
  }

  async resolveConnectionConflict(propertyId) {
    try {
      // First, disconnect current session completely
      const socket = this._socketsByProperty.get(propertyId);
      if (socket) {
        socket.disconnect();
        this._socketsByProperty.delete(propertyId);
      }
      
      // Clear conflict state
      runInAction(() => {
        this._connectionStates.set(propertyId, 'disconnected');
        this._subscriptionStates.set(propertyId, false);
        this.setSession(propertyId, { 
          status: 'disconnected',
          qrCode: null,
          user: null,
          conflictReason: null,
          conflictTimestamp: null
        });
      });

      // Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Try to restart the session
      const property = this.root?.property?.items?.find(p => p._id === propertyId);
      if (property?.phone) {
        // Start fresh session
        await this.startSession(propertyId, false);
        
        // Wait for session to initialize
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Subscribe to socket
        await this.subscribeSocket(propertyId, property.phone, false);
        
        if (this.root?.pushToastMessage) {
          this.root.pushToastMessage({
            message: 'Connection conflict resolved. Attempting to reconnect...',
            severity: 'info'
          });
        }
        
        return true;
      } else {
        throw new Error('Property phone number not found');
      }
      
    } catch (error) {
      if (this.root?.pushToastMessage) {
        this.root.pushToastMessage({
          message: `Failed to resolve conflict: ${error.message}`,
          severity: 'error'
        });
      }
      
      return false;
    }
  }

  // ===== Message Handlers =====
  async readMessages(propertyId, phoneNumber, messageKeys) {
    if (!this.userHasBaileyPermission) {
      return;
    }
    
    if (!propertyId || !phoneNumber || !messageKeys?.length) {
      return;
    }

    try {
      // Send read receipts to the server
      const { data } = await apiFetcher().post(`${API_URL}/messages/mark-read`, {
        propertyId,
        phoneNumber,
        messageKeys
      });
      
      // Update local message status
      runInAction(() => {
        messageKeys.forEach(key => {
          this._updateMessageReadStatus(key.remoteJid, propertyId, key.id);
        });
      });
      
      return data;
      
    } catch (error) {
      // console.error('❌ Failed to mark messages as read:', error);
      throw error;
    }
  }

  async markAsAnsweredChat(propertyId, chatId) {
    if (!this.userHasBaileyPermission) {
      return;
    }
    
    if (!propertyId || !chatId) {
      return;
    }

    // Optimistic update: apply changes locally first so UI updates immediately
    // Handle duplicate-number chats: update any chat that matches by chatId OR by normalized phone/tenantNumber
    const previous = [];
    runInAction(() => {
      const keys = [this._generatePropertyKey(propertyId, false), this._generatePropertyKey(propertyId, true)];
      keys.forEach((key) => {
        const chatList = this.chatsByPropertyId.get(key);
        if (chatList?.items) {
          // Normalize the incoming chatId phone for comparison
          const incomingPhone = this.extractPhoneFromJid(chatId || '');
          chatList.items.forEach(c => {
            let matched = false;
            try {
              if (c.chatId === chatId) matched = true;
              const existingPhone = this.extractPhoneFromJid(c.chatId || c.tenantNumber || c.name || '');
              if (!matched && existingPhone && incomingPhone && existingPhone === incomingPhone) matched = true;
              if (!matched && c.tenantNumber && String(c.tenantNumber) === String(chatId)) matched = true;
            } catch (err) {
              // ignore matching errors
            }

            if (matched) {
              previous.push({ chat: c, answered: c.answered, status: c.status, list: chatList });
              c.answered = true;
              c.status = 'answered';
              chatList.lastUpdated = Date.now();
            }
          });
        }
      });
    });

    try {
      await apiFetcher().post(`${API_URL}/messages/chats/mark-answered`, {
        propertyId,
        chatId
      });
    } catch (error) {
      // Rollback on error
      runInAction(() => {
        previous.forEach(rec => {
          try {
            rec.chat.answered = rec.answered;
            rec.chat.status = rec.status;
            if (rec.list) rec.list.lastUpdated = Date.now();
          } catch (err) {
            // ignore
          }
        });
      });
      throw error;
    }
  }

  async markAsUnansweredChat(propertyId, chatId) {
    if (!this.userHasBaileyPermission) {
      return;
    }

    if (!propertyId || !chatId) {
      return;
    }

    // Optimistic update: apply changes locally first so UI updates immediately
    // Handle duplicate-number chats: update any chat that matches by chatId OR by normalized phone/tenantNumber
    const previous = [];
    runInAction(() => {
      const keys = [this._generatePropertyKey(propertyId, false), this._generatePropertyKey(propertyId, true)];
      keys.forEach((key) => {
        const chatList = this.chatsByPropertyId.get(key);
        if (chatList?.items) {
          const incomingPhone = this.extractPhoneFromJid(chatId || '');
          chatList.items.forEach(c => {
            let matched = false;
            try {
              if (c.chatId === chatId) matched = true;
              const existingPhone = this.extractPhoneFromJid(c.chatId || c.tenantNumber || c.name || '');
              if (!matched && existingPhone && incomingPhone && existingPhone === incomingPhone) matched = true;
              if (!matched && c.tenantNumber && String(c.tenantNumber) === String(chatId)) matched = true;
            } catch (err) {
              // ignore matching errors
            }

            if (matched) {
              previous.push({ chat: c, answered: c.answered, status: c.status, list: chatList });
              c.answered = false;
              c.status = 'unanswered';
              chatList.lastUpdated = Date.now();
            }
          });
        }
      });
    });

    try {
      await apiFetcher().post(`${API_URL}/messages/chats/mark-unanswered`, {
        propertyId,
        chatId
      });
    } catch (error) {
      // Rollback on error
      runInAction(() => {
        previous.forEach(rec => {
          try {
            rec.chat.answered = rec.answered;
            rec.chat.status = rec.status;
            if (rec.list) rec.list.lastUpdated = Date.now();
          } catch (err) {
            // ignore
          }
        });
      });
      throw error;
    }
  }

  _updateMessageReadStatus(chatId, propertyId, messageId) {
    const chatKey = this._generateChatKey(chatId, propertyId);
    const entry = this.messagesByChatId.get(chatKey);
    
    if (!entry?.items) return;
    
    // Find and update the specific message
    const message = entry.items.find(m => {
      const msgId = m.id || m.messageId || m.whatsappMessageId;
      return msgId === messageId;
    });
    
    if (message && !message.fromMe) {
      message.read = true;
      message.status = 3; // Read status
    }
    
    // Update chat unread count
    const chatList = this.chatsByPropertyId.get(this._generatePropertyKey(propertyId, false));
    if (chatList?.items) {
      const chat = chatList.items.find(c => c.chatId === chatId);
      if (chat && chat.unreadCount > 0) {
        chat.unreadCount = Math.max(0, chat.unreadCount - 1);
      }
    }
  }

  _handleMessageReceived(msg) {
    let chatId = msg.key?.remoteJid?.endsWith('@lid') ? msg.key?.remoteJidAlt : msg.key?.remoteJid;
    const messageId = msg.id || msg.messageId || msg.whatsappMessageId;
    if (typeof chatId === 'string' && chatId.endsWith('@lid')) {
      let message;
      this.messagesByChatId.forEach(entry => {
        entry.items.filter(msg => {
          const msgId = msg.id || msg.messageId || msg.whatsappMessageId;
          if (msgId === messageId) {
            message = msg;
          }
        });
      });

      chatId = this.getChatId(message.key);
    }

    const propertyId = msg.propertyId;
    const chatKey = this._generateChatKey(chatId, propertyId);

    runInAction(() => {
      // Initialize chat entry if it doesn't exist
      if (!this.messagesByChatId.has(chatKey)) {
        this.messagesByChatId.set(chatKey, { 
          items: observable.array([]), 
          isLoading: false 
        });
      }
      
      const entry = this.messagesByChatId.get(chatKey);
      
      // Normalize the message
      const normalizedMsg = observable.object({
        ...msg,
        id: msg.id || msg.messageId || msg.whatsappMessageId || `msg_${Date.now()}`,
        timestamp: msg.timestamp || new Date().toISOString(),
        content: msg.content ? observable.object({
          ...msg.content,
          // FIX: Set presignUrl immediately if URL is already presigned
          presignUrl: this._isPresignedUrl(msg.content.url) ? msg.content.url : null,
          isLoadingPresign: false
        }) : observable.object({}),
        fromMe: Boolean(msg.fromMe),
        type: msg.type || 'text'
      });

      // Try to match with optimistic message first
      const optimisticIndex = this._findOptimisticMessage(entry.items, normalizedMsg);

      if (optimisticIndex !== -1) {
        this._updateOptimisticMessage(entry.items, optimisticIndex, normalizedMsg);
      } else {
        // Check for duplicates
        const existingIndex = entry.items.findIndex(m => {
          const existingId = m.id || m.messageId || m.whatsappMessageId;
          const newId = normalizedMsg.id || normalizedMsg.messageId || normalizedMsg.whatsappMessageId;
          return existingId && newId && existingId === newId;
        });
        
        if (existingIndex === -1) {
          // Use MobX array methods for proper reactivity
          entry.items.push(normalizedMsg);
          
          // Force reactivity by updating a timestamp or counter
          entry.lastUpdated = Date.now();
        } else {
          // Replace the entire item to ensure reactivity
          entry.items[existingIndex] = normalizedMsg;
          entry.lastUpdated = Date.now();
        }
      }

      // Update chat list with latest message info
      this._updateChatList(propertyId, chatId, normalizedMsg);
    });

    // Fetch presigned URLs for any new media content
    if (msg.content?.url && 
      ['image', 'video', 'audio', 'document'].includes(msg.type) &&
      !this._isPresignedUrl(msg.content.url)) {
    
      setTimeout(() => {
        this.fetchPresignUrlsForMessages(chatKey);
      }, 100);
    }

    this._scheduleFetchChats(propertyId);
    this._showDesktopNotification(msg);
  }

  _findOptimisticMessage(items, newMsg) {
    return items.findIndex(m => {
      if (!m.isOptimistic || m.fromMe !== newMsg.fromMe) return false;

      return (
        (m.jobId && newMsg.jobId && m.jobId === newMsg.jobId) ||
        (m.content?.text && newMsg.content?.text && 
         m.content.text.trim() === newMsg.content.text.trim() &&
         Math.abs(new Date(m.timestamp) - new Date(newMsg.timestamp)) < 300000) ||
        (m.type === newMsg.type && 
         ['image', 'video', 'audio', 'document'].includes(m.type) &&
         Math.abs(new Date(m.timestamp) - new Date(newMsg.timestamp)) < 300000)
      );
    });
  }

  _updateOptimisticMessage(items, index, newMsg) {
    const optimistic = items[index];
    const preservedBlob = optimistic.content?.isLocalBlob ? optimistic.content.presignUrl : null;

    items[index] = {
      ...optimistic,
      ...newMsg,
      isOptimistic: false,
      content: {
        ...newMsg.content,
        ...(preservedBlob && newMsg.content?.url ? {
          presignUrl: preservedBlob,
          isLocalBlob: true,
          s3Url: newMsg.content.url,
          url: newMsg.content.url
        } : {})
      }
    };
  }

  _addOrUpdateMessage(items, msg) {
    const id = msg.id ?? msg.messageId ?? msg.whatsappMessageId;
    const existingIndex = items.findIndex(m => {
      const mId = m.id ?? m.messageId ?? m.whatsappMessageId;
      return mId === id;
    });

    if (existingIndex === -1) {
      items.push(msg);
    } else {
      items[existingIndex] = { ...items[existingIndex], ...msg };
    }
  }

  _handleMessageStatusUpdate(updates) {
    const updateList = Array.isArray(updates) ? updates : [updates];

    runInAction(() => {
      updateList.forEach(update => this._processStatusUpdate(update));
    });
  }

  _processStatusUpdate(update) {
    const { key, update: fields } = update;
    const propertyId = update.propertyId;
    const remoteJid = key.remoteJid;
    const msgId = key.id;

    // Find matching messages across all chats
    this.messagesByChatId.forEach((entry, chatKey) => {
      if (!chatKey.includes(`-${propertyId}`) || !entry?.items) return;

      entry.items.forEach(msg => {
        if (this._messageMatches(msg, msgId, update.jobId)) {
          if (fields.status !== undefined) {
            const currentStatus = msg.status;
            const newStatus = fields.status;
            
            // Check for status downgrade
            if (this._isStatusDowngrade(currentStatus, newStatus)) {
              // Update other fields but skip status
              const fieldsWithoutStatus = { ...fields };
              delete fieldsWithoutStatus.status;
              Object.assign(msg, fieldsWithoutStatus);
              
              return; // Skip status update
            }
          }

          Object.assign(msg, fields);
          if (fields.status !== undefined) {
            msg.status = fields.status;
            msg.isOptimistic = false;
          }
        }
      });
    });

    // Update chat status
    this._updateChatStatus(propertyId, remoteJid, msgId, fields);
  }

  _updateChatStatus(propertyId, chatId, messageId, fields) {
    const key = this._generatePropertyKey(propertyId, false);
    const chatList = this.chatsByPropertyId.get(key);

    if (!chatList?.items) return;

    // Check if chatId ends with @lid
    if (typeof chatId === 'string' && chatId.endsWith('@lid')) {
      let message;
      this.messagesByChatId.forEach(entry => {
        entry.items.filter(msg => {
          const msgId = msg.id || msg.messageId || msg.whatsappMessageId;
          if (msgId === messageId) {
            message = msg;
          }
        });
      });

      if (message && message.key) {
        chatId = this.getChatId(message?.key);
      }
    }
    chatId = this.cleanJid(chatId);

    const existingChat = chatList.items.find(item => item.chatId === chatId);
    
    if (existingChat) {
      runInAction(() => {
        // ===== FIX: Always update the chat status if it's for the last message =====
        if (existingChat.lastMessageId && String(existingChat.lastMessageId) === String(messageId)) {
          existingChat.status = fields.status;
          
          // Force reactivity by updating lastUpdated
          chatList.lastUpdated = Date.now();
        } 
      });
    } else {
      // console.warn(`Chat ${chatId} not found for status update`);
    }
  }

  _updateChatList(propertyId, chatId, message) {
    const key = this._generatePropertyKey(propertyId, false);
    const chatList = this.chatsByPropertyId.get(key);
    
    if (!chatList?.items) return;

    const chatIndex = chatList.items.findIndex(chat => chat.chatId === chatId);
    if (chatIndex !== -1) {
      const chat = chatList.items[chatIndex];
      
      runInAction(() => {
        // Update last message info
        chat.lastMessage = this._formatLastMessage(message);
        chat.lastMessageAt = message.timestamp;
        chat.fromMe = message.fromMe;
        chat.status = message.status || 0;
        
        // Update lastMessageId
        const messageId = message.id || message.messageId || message.whatsappMessageId;
        if (messageId) {
          chat.lastMessageId = String(messageId);
        }
        
        // Update unread count
        if (!message.fromMe && !message.isOptimistic) {
          chat.unreadCount = (chat.unreadCount || 0) + 1;
        }
        
        // Move to top of list
        chatList.items.splice(chatIndex, 1);
        chatList.items.unshift(chat);
        
        this._sortChatsByTimestamp(chatList.items);

        // Force reactivity
        chatList.lastUpdated = Date.now();
      });
    } else {
      // console.warn(`Chat ${chatId} not found in property ${propertyId} chat list`);
      // ===== FIX: Create new chat entry if it doesn't exist =====
      runInAction(() => {
        const messageId = message.id || message.messageId || message.whatsappMessageId;
        const newChat = {
          chatId: chatId,
          name: this._extractNameFromMessage(message) || chatId,
          lastMessage: this._formatLastMessage(message),
          lastMessageAt: message.timestamp,
          lastMessageId: messageId ? String(messageId) : null,
          fromMe: message.fromMe,
          status: message.status || 0,
          unreadCount: message.fromMe ? 0 : 1,
          propertyId: propertyId,
          isGroup: false
        };
        
        chatList.items.unshift(newChat);
        this._sortChatsByTimestamp(chatList.items);
      
        // Force reactivity
        chatList.lastUpdated = Date.now();
      });
    }
  }

  _sortChatsByTimestamp(chats) {
    chats.replace(
      chats.slice().sort((a, b) => {
        const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return timeB - timeA; // Newest first (descending order)
      })
    );
  }

  _messageMatches(message, msgId, jobId) {
    const messageIds = [
      message.id, message.messageId, message.whatsappMessageId,
      message.key?.id, message.jobId
    ].filter(Boolean).map(String);

    return (
      (msgId && messageIds.some(id => id === msgId || id.includes(msgId))) ||
      (jobId && message.jobId === jobId)
    );
  }

  // ===== Media Content Management =====
  async fetchPresignUrlsForMessages(chatKey) {
    const entry = this.messagesByChatId.get(chatKey);
    if (!entry?.items) return;

    const mediaMessages = entry.items.filter(msg => 
      msg.content?.url && 
      !msg.content?.presignUrl && 
      !msg.content?.isLoadingPresign &&
      ['image', 'video', 'audio', 'document'].includes(msg.type)
    );

    if (mediaMessages.length === 0) return;

    // Process in batches to avoid overwhelming the server
    for (let i = 0; i < mediaMessages.length; i += PRESIGN_BATCH_SIZE) {
      const batch = mediaMessages.slice(i, i + PRESIGN_BATCH_SIZE);
      await this._processBatchPresignUrls(batch, chatKey);
      
      // Add small delay between batches
      if (i + PRESIGN_BATCH_SIZE < mediaMessages.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  async _processBatchPresignUrls(messages, chatKey) {
    const promises = messages.map(async (msg) => {
      try {
        // Mark as loading
        runInAction(() => {
          if (msg.content) {
            msg.content.isLoadingPresign = true;
            msg.content.presignError = null;
          }
        });

        const presignedUrl = await this._fetchSinglePresignUrl(msg.content.url);
        
        runInAction(() => {
          if (msg.content) {
            msg.content.presignUrl = presignedUrl;
            msg.content.isLoadingPresign = false;
          }
        });

      } catch (error) {
        runInAction(() => {
          if (msg.content) {
            msg.content.isLoadingPresign = false;
            msg.content.presignError = error.message;
          }
        });
      }
    });

    await Promise.allSettled(promises);
  }

  async _fetchSinglePresignUrl(s3Url) {
    if (!s3Url) throw new Error('No S3 URL provided');
    
    try {
      const presignedUrl = await getPresignedUrl({ documentCategory: 'whatsapp_media', key: s3Url });
      return presignedUrl;
    } catch (error) {
      throw new Error(`Failed to get presigned URL: ${error.message}`);
    }
  }

  // ===== Media URL Helpers =====
  getMediaUrl(message) {
    if (!message?.content) return null;
    
    // Prefer presigned URL, fallback to local blob, then S3 URL
    return message.content.presignUrl || 
          message.content.s3Url || 
          message.content.url ||
          null;
  }

  isMediaLoading(message) {
    return Boolean(message?.content?.isLoadingPresign);
  }

  hasMediaError(message) {
    return Boolean(message?.content?.presignError);
  }

  getMediaError(message) {
    return message?.content?.presignError || null;
  }

  // ===== Public API Methods =====
  async subscribeSocket(propertyId, phoneNumber, isSuperkos = false) {
    if (!propertyId) return;

    if (isSuperkos) {
      return null; // Return early for Superkos
    }

    if (!this.userHasBaileyPermission) {
      return null;
    }

    try {
      const socket = await this.ensureSocket(propertyId);
      
      // Mark as subscribed BEFORE emitting to prevent race conditions
      this._subscriptionStates.set(propertyId, true);
      
      socket.emit('subscribe', { 
        phoneNumber: String(phoneNumber), 
        propertyId: String(propertyId)
      });
      
      // Also store the phone number for auto-resubscription
      this.setSession(propertyId, { phoneNumber: String(phoneNumber) });
      
    } catch (error) {
      // Reset subscription state on error
      this._subscriptionStates.set(propertyId, false);
      // console.error(`Failed to subscribe to ${propertyId}:`, error);
      throw error;
    }
  }

  async startSession(propertyId, isSuperkos = false) {
    if (isSuperkos) {
      this.setSession(propertyId, { 
        status: 'superkos',
        isSuperkos: true 
      });
      return { data: { state: 'superkos' } };
    }
    
    const cacheKey = `start-session-${propertyId}`;
    
    if (this._activeRequests.has(cacheKey)) {
      return this._activeRequests.get(cacheKey);
    }

    const request = this._executeStartSession(propertyId);
    this._activeRequests.set(cacheKey, request);
    
    try {
      return await request;
    } finally {
      this._activeRequests.delete(cacheKey);
    }
  }

  async _executeStartSession(propertyId) {
    try {
      await this.ensureSocket(propertyId);

      const { data } = await apiFetcher().post(`${API_URL}/session/connect`, {
        propertyId
      });
      
      this.setSession(propertyId, { 
        status: data?.data?.status?.sessionStatus || 'subscribed',
        qrCode: data?.data?.qrCode || null, // Clear any existing QR code when starting new session
        user: null    // Clear any existing user info
      });
      
      // Try to auto-subscribe the socket for this property so the server
      try {
        const property = this.root?.property?.items?.find(p => p._id === propertyId);
        const phone = property?.phone || data?.data?.phoneNumber;
        if (phone) {
          // subscribeSocket is idempotent and guarded; ignore errors here
          await this.subscribeSocket(propertyId, phone, false);
        }
      } catch (err) {
        // Non-fatal: log for debugging but don't throw
        console.warn(`Failed to auto-subscribe after startSession for ${propertyId}:`, err);
      }
      
      return data;
    } catch (error) {
      this.setSession(propertyId, { 
        status: 'ERROR',
        qrCode: null,
        user: null
      });
      throw error;
    }
  }

  async fetchChats(propertyId, isSuperkos = false) {
    const cacheKey = `fetch-chats-${propertyId}-${isSuperkos}`;
    
    if (this._activeRequests.has(cacheKey)) {
      return this._activeRequests.get(cacheKey);
    }

    const request = isSuperkos ? 
      this._fetchChatsSuperkos(propertyId) : 
      this._fetchChatsSocket(propertyId);
    
    this._activeRequests.set(cacheKey, request);
    
    try {
      return await request;
    } finally {
      this._activeRequests.delete(cacheKey);
    }
  }

  async _fetchChatsSocket(propertyId) {
    if (!this.userHasBaileyPermission) {
      throw new Error('Bailey permission required to fetch chats via socket');
    }

    const socket = this._socketsByProperty.get(propertyId);
    if (!socket) throw new Error('Socket not available');

    try {
      const { data } = await apiFetcher().get(`${API_URL}/messages/chats`, {
        params: { propertyId }
      });

      runInAction(() => {
        const chats = data?.data?.chats || [];
        const normalized = chats.map(chat => this._normalizeChat(chat));

        // Sort by timestamp (newest first)
        const sortedChats = normalized.sort((a, b) => {
          const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
          const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
          return timeB - timeA;
        });
        
        const key = this._generatePropertyKey(propertyId, false);
        this.chatsByPropertyId.set(key, observable.object({
          items: observable.array(sortedChats.map(chat => observable.object(chat))),
          lastUpdated: Date.now()
        }));
      });

      return this.getChats(propertyId, false);
    } catch (error) {
      // console.error('Failed to fetch chats via API:', error);
      throw error;
    }
  }

  async _fetchChatsSuperkos(propertyId) {
    const cacheKey = `fetch-chats-superkos-${propertyId}`;

    if (this._activeRequests.has(cacheKey)) {
      return this._activeRequests.get(cacheKey);
    }

    const request = (async () => {
      const { data } = await apiFetcher().get(`${API_URL}/messages/chats/superkos`, {
        params: { propertyId }
      });

      runInAction(() => {
        const chats = data?.data?.chats || [];
        const normalized = chats.map(chat => this._normalizeChat(chat));
        
        // Sort by timestamp (newest first)
        const sortedChats = normalized.sort((a, b) => {
          const timeA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
          const timeB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
          return timeB - timeA;
        });

        sortedChats.forEach(chat => {
          chat.status = 'answered';
        });
        
        const key = this._generatePropertyKey(propertyId, true);
        this.chatsByPropertyId.set(key, observable.object({
          items: observable.array(sortedChats.map(chat => observable.object(chat))),
          lastUpdated: Date.now()
        }));
      });

      return this.getChats(propertyId, true);
    })();
    this._activeRequests.set(cacheKey, request);

    try {
      return await request;
    } finally {
      this._activeRequests.delete(cacheKey);
    }
  }

  async fetchChatMessages(propertyId, chatId) {
    const chatKey = this._generateChatKey(chatId, propertyId);
    if (this._messagesFetchedForChat.has(chatKey)) {
      return this.getMessages(chatId, propertyId);
    }
    
    try {
      const { data } = await apiFetcher().get(`${API_URL}/messages`, {
        params: { propertyId, chatId }
      });
      
      
      runInAction(() => {
        const messages = data?.data?.messages || [];
        const entry = this.messagesByChatId.get(chatKey) || 
          this.messagesByChatId.set(chatKey, { items: observable.array([]), isLoading: false }).get(chatKey);

        const normalizedMessages = messages.map(msg => this._normalizeMessage(msg));
        entry.items.replace(normalizedMessages);
        this._messagesFetchedForChat.add(chatKey);
      });

      // Fetch presigned URLs for media messages after a short delay
      setTimeout(() => {
        this.fetchPresignUrlsForMessages(chatKey);
      }, 100);

      return this.getMessages(chatId, propertyId);
    } catch (error) {
      // console.error(`Failed to fetch messages for chat ${chatId} in property ${propertyId}:`, error);
      throw error;
    }
  }

  async fetchChatMessagesSuperkos(propertyId, chatId) {
    const chatKey = this._generateChatKey(chatId, propertyId, true);
    if (this._messagesFetchedForChat.has(chatKey)) {
      return this.getMessages(chatId, propertyId, true);
    }
    try {
      const { data } = await apiFetcher().get(`${API_URL}/messages/superkos`, {
        params: { propertyId, chatId }
      });
      runInAction(() => {
        const messages = data?.data?.messages || [];
        const entry = this.messagesByChatId.get(chatKey) ||
          this.messagesByChatId.set(chatKey, { items: observable.array([]), isLoading: false }).get(chatKey);
        const normalizedMessages = messages.map(msg => this._normalizeMessage(msg));
        entry.items.replace(normalizedMessages);
        this._messagesFetchedForChat.add(chatKey);
      });

      return this.getMessages(chatId, propertyId, true);
    } catch (error) {
      // console.error(`Failed to fetch messages for chat ${chatId} in property ${propertyId}:`, error);
      throw error;
    }
  }

  async sendMessage(propertyId, payload, chatId) {
    const tempId = `temp_${Date.now()}_${Math.random()}`;

    if (!this.userHasBaileyPermission) {
      throw new Error('Bailey permission required to send messages');
    }
    
    try {
      // Create optimistic message
      this._createOptimisticMessage(chatId, propertyId, {
        ...payload,
        tempId,
        type: 'text'
      });

      // Send to server
      const { data } = await apiFetcher().post(`${API_URL}/messages/send`, {
        ...payload,
        propertyId
      });

      // Update with job ID
      this._updateOptimisticWithJobId(chatId, propertyId, tempId, data.data?.jobId);

      return data;
    } catch (error) {
      this._markOptimisticAsFailed(chatId, propertyId, tempId, error);
      throw error;
    }
  }

  async sendMediaMessage(propertyId, formData, chatId, mediaInfo) {
    const tempId = `temp_media_${Date.now()}_${Math.random()}`;

    if (!this.userHasBaileyPermission) {
      throw new Error('Bailey permission required to send media messages');
    }
    
    try {
      // Create optimistic media message
      this._createOptimisticMessage(chatId, propertyId, {
        tempId,
        type: mediaInfo.type,
        content: {
          presignUrl: mediaInfo.localUrl,
          isLocalBlob: true,
          mediaData: mediaInfo
        }
      });

      const { data } = await apiFetcher().post(`${API_URL}/messages/send-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      this._updateOptimisticWithJobId(chatId, propertyId, tempId, data.data?.jobId);
      
      return data;
    } catch (error) {
      this._markOptimisticAsFailed(chatId, propertyId, tempId, error);
      throw error;
    }
  }

  // ===== Helper Methods =====
  _createOptimisticMessage(chatId, propertyId, messageData) {
    runInAction(() => {
      const key = this._generateChatKey(chatId, propertyId);
      const entry = this.messagesByChatId.get(key) || 
        this.messagesByChatId.set(key, { items: observable.array([]) }).get(key);

      const optimisticMessage = {
        fromMe: true,
        sender: 'me',
        sentBy: 'user',
        messageId: messageData.tempId,
        id: messageData.tempId,
        propertyId,
        timestamp: new Date().toISOString(),
        status: 'sending',
        whatsappChatId: chatId,
        isOptimistic: true,
        jobId: null,
        content: messageData.content || {},
        provider: 'bailey',
        ...messageData
      };

      entry.items.push(optimisticMessage);

      this._updateChatList(propertyId, chatId, optimisticMessage);
    });
  }

  _updateOptimisticWithJobId(chatId, propertyId, tempId, jobId) {
    runInAction(() => {
      const key = this._generateChatKey(chatId, propertyId);
      const entry = this.messagesByChatId.get(key);
      
      if (entry?.items) {
        const msgIndex = entry.items.findIndex(m => m.messageId === tempId);
        if (msgIndex !== -1) {
          entry.items[msgIndex].jobId = jobId;
          entry.items[msgIndex].status = 'queued';
        }
      }
    });
  }

  _markOptimisticAsFailed(chatId, propertyId, tempId, error) {
    runInAction(() => {
      const key = this._generateChatKey(chatId, propertyId);
      const entry = this.messagesByChatId.get(key);
      
      if (entry?.items) {
        const msgIndex = entry.items.findIndex(m => m.messageId === tempId);
        if (msgIndex !== -1) {
          entry.items[msgIndex].status = 'failed';
          entry.items[msgIndex].error = error.message;
          entry.items[msgIndex].isOptimistic = false;
        }
      }
    });
  }

  _scheduleFetchChats(propertyId, delay = FETCH_DELAY) {
    if (!propertyId) return;
    console.log(`Scheduling fetchChats for property ${propertyId} in ${delay}ms`);
    
    const existingTimer = this._fetchTimers.get(propertyId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.fetchChats(propertyId);
    }, delay);

    this._fetchTimers.set(propertyId, timer);
  }

  _normalizeChat(data) {
    const chatId = data?.whatsappChatId || 'unknown';
    const name = data?.displayName || data?.tenantNumber || chatId;
    
    return {
      chatId,
      name,
      unreadCount: Number(data?.unreadCount) || 0,
      lastMessageAt: data?.lastMessageAt || null,
      messageId: data?.messageId || '',
      tenantNumber: data?.tenantNumber || null,
      isGroup: Boolean(data?.isGroup),
      lastMessage: this._formatLastMessage(data?.lastMessage),
      lastMessageId: data?.lastMessage?.id || data?.lastMessage?.messageId || '',
      lastMessageStatus: data?.lastMessage?.status || 0,
      status: data?.status || 0,
      propertyId: data?.propertyId || '',
      tenantId: data?.tenantId || '',
      fromMe: Boolean(data?.lastMessage?.fromMe)
    };
  }

  _normalizeMessage(msg) {
    return observable.object({
      ...msg,
      id: msg.id || msg.messageId || msg.whatsappMessageId || `msg_${Date.now()}`,
      timestamp: msg.timestamp || new Date().toISOString(),
      content: msg.content ? observable.object({
        ...msg.content,
        // Initialize media loading state
        isLoadingPresign: msg.content.url && !msg.content.presignUrl ? false : undefined,
        presignError: null
      }) : observable.object({}),
      fromMe: Boolean(msg.fromMe),
      type: msg.type || 'text',
      status: msg.status || (msg.fromMe ? 1 : 0)
    });
  }

  _formatLastMessage(lastMessage) {
    if (!lastMessage) return '';

    // Handle different message types
    const type = lastMessage.type?.toLowerCase() || 'text';
    const content = typeof lastMessage.content === 'string' ? lastMessage.content.trim() : (lastMessage.content?.text || lastMessage.content?.caption || '').trim();

    // For text messages, show the actual text
    if (type === 'text' && content) {
      return stripRichFormatting(content.trim());
    }
    
    // For media messages, show type with emoji
    const mediaTypes = {
      image: '📷 Image',
      video: '🎥 Video',  
      audio: '🎵 Audio',
      voice: '🎤 Voice message',
      document: '📄 Document',
      sticker: '😊 Sticker',
      location: '📍 Location',
      contact: '👤 Contact',
      text: ''
    };
    
    const mediaLabel = mediaTypes[type] || ``;
    
    // Add caption if available
    if (content) {
      const caption = content;
      const shortCaption = caption.length > 30 ? `${caption.substring(0, 30)}...` : caption;
      return `${mediaLabel}: ${shortCaption}`;
    }
    
    return mediaLabel;
  }

  // ===== Getters =====
  getChats(propertyId, isSuperkos = false) {
    const key = this._generatePropertyKey(propertyId, isSuperkos);
    return this.chatsByPropertyId.get(key)?.items || [];
  }

  async refreshTenantsForAllProperties() {
    const properties = this.root?.property?.items || [];
    await Promise.all(properties.map(p => this.refreshTenants(p._id)));
  }

  async refreshTenants(propertyId) {
    // Guard: skip if already refreshing for this property
    if (this._refreshingTenants.has(propertyId)) return;
    this._refreshingTenants.add(propertyId);

    try {
      const { data } = await this.root.tenant.fetch({ property: propertyId }, { setItem: false });
      if (!data || !Array.isArray(data)) return;

      const processed = new Map();
      for (const t of data) {
        const phoneSet = new Set(
          (t.contacts || [])
            .map(c => this.normalizePhone(c.phone))
            .filter(Boolean)
        );
        processed.set(String(t._id), {
          id: String(t._id),
          phones: phoneSet
        });
      }

      // ✅ FIX: Write into observable map so getTotalUnanswered() reacts
      runInAction(() => {
        this.tenantSnapshotByProperty.set(propertyId, processed);
      });
    } finally {
      this._refreshingTenants.delete(propertyId);
    }
  }

  // Compute total unanswered chats across all properties using consistent rules
  getTotalUnanswered() {
    if (!this.userHasBaileyPermission) return 0;
    try {
      const properties = this.root?.property?.items || [];
      const tenantsByProperty = this.tenantSnapshotByProperty;
      const chatsMap = this.chatsByPropertyId;
      let total = 0;

      properties.forEach(property => {
        const propertyId = String(property._id);
        const session = this.getSession(propertyId) || {};
        const isConnected = session.status === 'connected';
        if (!isConnected) return;

        const tenantMap = tenantsByProperty.get(propertyId);
        if (!tenantMap) return;
        
        const chatKey = this._generatePropertyKey(propertyId, false);
        const chatList = chatsMap.get(chatKey);
        if (!chatList?.items) return;

        chatList.items.forEach(chat => {
          if (!chat) return;
          const statusStr = String(chat.status || '').toLowerCase();
          
          if (statusStr !== 'unanswered') return;
          const tenant = tenantMap.get(String(chat.tenantId));

          if (!tenant) return;
          const cleanedNumber = String(chat.tenantNumber || '').replace(/[^\d]/g, '');
          if (!tenant.phones.has(cleanedNumber)) return;
          
          total++;
        });
      });

      return total;
    } catch (error) {
      // console.error('Error calculating total unanswered chats:', error);
      return 0;
    }
  }

  getMessages(chatId, propertyId, isSuperkos = false) {
    const key = this._generateChatKey(chatId, propertyId, isSuperkos);
    const items = this.messagesByChatId.get(key)?.items || [];
    
    // Remove duplicates by ID
    const seen = new Set();
    return items.filter(msg => {
      const id = msg.id ?? msg.messageId ?? msg.whatsappMessageId;
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  isLoading(chatId, propertyId, isSuperkos = false) {
    const key = this._generateChatKey(chatId, propertyId, isSuperkos);
    return Boolean(this.messagesByChatId.get(key)?.isLoading);
  }

  getConnectionState(propertyId) {
    return this._connectionStates.get(propertyId) || 'disconnected';
  }

  isSubscribed(propertyId) {
    return Boolean(this._subscriptionStates.get(propertyId));
  }


  // ===== Broadcast Storage Management =====
  
  _generateBroadcastKey(listId, propertyId, isSuperkos = false) {
    const suffix = isSuperkos ? '-superkos' : '';
    return `${BROADCAST_STORAGE_PREFIX}${listId}-${propertyId}${suffix}`;
  }

  _generateBroadcastListKey(propertyId) {
    return `${BROADCAST_LIST_STORAGE_PREFIX}${propertyId}`;
  }

  _generateBroadcastMessageKey(chatKey) {
    return `${BROADCAST_MESSAGE_STORAGE_PREFIX}${chatKey}`;
  }

  // ===== Broadcast Lists Management =====

  setBroadcastLists(propertyId, lists) {
    runInAction(() => {
      this.broadcastLists.set(propertyId, observable.array(
        lists.map(list => observable.object(list))
      ));
    });
    
    // Save to localStorage with error handling
    this._saveBroadcastListsToStorage(propertyId, lists);
  }

  getBroadcastLists(propertyId) {
    let lists = this.broadcastLists.get(propertyId);
    
    if (!lists) {
      // Try to load from localStorage
      const stored = this._loadBroadcastListsFromStorage(propertyId);
      if (stored.length > 0) {
        this.setBroadcastLists(propertyId, stored);
        lists = this.broadcastLists.get(propertyId);

        // Auto-load messages for each broadcast list
        setTimeout(() => {
          stored.forEach(list => {
            const chatId = `broadcast_${list.id}`;
            this.loadBroadcastMessagesIfNeeded(chatId, propertyId, false);
          });
        }, 0);
      } else {
        lists = observable.array([]);
        runInAction(() => {
          this.broadcastLists.set(propertyId, lists);
        });
      }
    } else {
      // Even if lists exist, ensure messages are loaded
      setTimeout(() => {
        lists.forEach(list => {
          const chatId = `broadcast_${list.id}`;
          this.loadBroadcastMessagesIfNeeded(chatId, propertyId, false);
        });
      }, 0);
    }
    
    return lists;
  }

  addBroadcastList(propertyId, broadcastList) {
    runInAction(() => {
      const lists = this.getBroadcastLists(propertyId);
      const existingIndex = lists.findIndex(list => list.id === broadcastList.id);
      
      if (existingIndex !== -1) {
        // Update existing
        lists[existingIndex] = observable.object(broadcastList);
      } else {
        // Add new
        lists.push(observable.object(broadcastList));
      }
    });
    
    // Save updated lists
    this._saveBroadcastListsToStorage(propertyId, this.getBroadcastLists(propertyId).slice());
  }

  getBroadcastChatInfo(chatId, propertyId) {
    if (!this.isBroadcastChat(chatId)) return null;
    
    const listId = chatId.replace('broadcast_', '');
    const broadcastList = this.getBroadcastListById(propertyId, listId);
    
    if (!broadcastList) return null;
    
    return {
      id: chatId,
      name: broadcastList.name,
      phones: broadcastList.phones || [],
      contactCount: (broadcastList.phones || []).length,
      isBroadcast: true,
      broadcastList: broadcastList
    };
  }

  removeBroadcastList(propertyId, listId) {
    runInAction(() => {
      const lists = this.getBroadcastLists(propertyId);
      const index = lists.findIndex(list => list.id === listId);
      
      if (index !== -1) {
        lists.splice(index, 1);
      }
    });
    
    // Clean up associated messages
    const chatKey = this._generateBroadcastKey(listId, propertyId);
    this.messagesByChatId.delete(chatKey);
    
    // Also remove from localStorage
    this._removeBroadcastMessagesFromStorage(chatKey);

    // Save updated lists
    this._saveBroadcastListsToStorage(propertyId, this.getBroadcastLists(propertyId).slice());
  }

  _saveBroadcastListsToStorage(propertyId, lists) {
    try {
      const key = this._generateBroadcastListKey(propertyId);
      const dataToStore = {
        lists,
        lastUpdated: new Date().toISOString(),
        version: '1.0'
      };
      
      localStorage.setItem(key, JSON.stringify(dataToStore));
    } catch (error) {
      // console.error('Failed to save broadcast lists:', error);
    }
  }

  _loadBroadcastListsFromStorage(propertyId) {
    try {
      const key = this._generateBroadcastListKey(propertyId);
      const stored = localStorage.getItem(key);
      
      if (!stored) return [];
      
      const data = JSON.parse(stored);
      
      // Handle both old and new format
      if (Array.isArray(data)) {
        return data; // Old format
      } else if (data.lists && Array.isArray(data.lists)) {
        return data.lists; // New format
      }
      
      return [];
    } catch (error) {
      // console.error('Failed to load broadcast lists:', error);
      return [];
    }
  }

  // ===== Broadcast Messages Management =====

  async sendBroadcastMessage(propertyId, payload) {
    const tempId = `temp_broadcast_${Date.now()}_${Math.random()}`;
    let msg = null;
    
    try {
      // Step 1: Store optimistic broadcast message
      if (payload.listId) {
        msg = this._createOptimisticBroadcastMessage(payload.listId, propertyId, {
          tempId,
          text: payload.text,
          phones: payload.recipients,
          listId: payload.listId
        });
      }

      // Step 2: Send to API
      const { data } = await apiFetcher().post(`${API_URL}/broadcast/send`, {
        ...payload,
        propertyId,
      });
      
      // Step 3: Update optimistic message with job info
      if (payload.listId) {
        this._updateOptimisticBroadcastMessage(payload.listId, propertyId, tempId, {
          status: "sent",
          serverResponse: data.data,
          jobId: data.data?.jobId
        });
      }
      
      return data;
    } catch (err) {
      // // Mark as failed
      // if (payload.listId) {
      //   this._updateOptimisticBroadcastMessage(payload.listId, propertyId, tempId, {
      //     status: "failed",
      //     error: err.message
      //   });
      // }

      // Delete failed optimistic message   
      if (msg) {
        runInAction(() => {
          const key = this._generateBroadcastKey(msg.content.listId, propertyId, false);
          this.messagesByChatId.delete(key);
          this._removeBroadcastMessagesFromStorage(key);
        });
      }

      throw err;
    }
  }

  _createOptimisticBroadcastMessage(listId, propertyId, messageData) {
    const chatId = `broadcast_${listId}`;
    const key = this._generateBroadcastKey(listId, propertyId, false);

    let broadcastMsg = null;
    
    runInAction(() => {
      const entry = this.messagesByChatId.get(key);
      
      broadcastMsg = {
        fromMe: true,
        sender: "me",
        sentBy: "user",
        messageId: messageData.tempId,
        id: messageData.tempId,
        propertyId: propertyId,
        timestamp: new Date().toISOString(),
        status: "sending",
        whatsappChatId: chatId,
        type: "broadcast",
        content: {
          text: messageData.text,
          recipients: messageData.phones || [],
          listId: listId,
          recipientCount: (messageData.phones || []).length
        },
        isBroadcast: true,
        isOptimistic: true,
        jobId: null,
      };
      
      // Use push instead of replace to maintain reactivity
      entry.items.push(observable.object(broadcastMsg));

      this.messagesByChatId.set(key, entry);
      
      // Save to storage immediately
      this._saveBroadcastMessagesToStorage(key);
    });

    return broadcastMsg;
  }

  _updateOptimisticBroadcastMessage(listId, propertyId, tempId, updates) {
    const key = this._generateBroadcastKey(listId, propertyId);
    
    runInAction(() => {
      const entry = this.messagesByChatId.get(key);
      if (!entry?.items) return;
      
      const msgIndex = entry.items.findIndex(m => m.messageId === tempId);
      if (msgIndex === -1) return;
      
      // Update message
      Object.assign(entry.items[msgIndex], updates, {
        isOptimistic: false
      });
    });
    
    // Save updated state
    this._saveBroadcastMessagesToStorage(key);
  }

  loadBroadcastMessagesIfNeeded(chatId, propertyId, isSuperkos = false) {
    // Extract listId from chatId (format: "broadcast_<listId>")
    const listId = chatId.replace('broadcast_', '');
    const key = this._generateBroadcastKey(listId, propertyId, isSuperkos);
    
    runInAction(() => {
      let entry = this.messagesByChatId.get(key);
      
      if (!entry) {
        // Initialize if doesn't exist
        entry = { 
          items: observable.array([]),
          isLoading: false,
          isLoaded: false
        };
        this.messagesByChatId.set(key, entry);
      }
      
      if (!entry.isLoaded) {
        const storedMessages = this._loadBroadcastMessagesFromStorage(key);
        
        if (storedMessages.length > 0) {
          entry.items.replace(storedMessages.map(msg => observable.object(msg)));
        }
        
        entry.isLoaded = true;
        
        this.messagesByChatId.set(key, entry);
      }
    });
  }

  _saveBroadcastMessagesToStorage(chatKey) {
    try {
      const entry = this.messagesByChatId.get(chatKey);
      if (!entry?.items) return;

      const storageKey = this._generateBroadcastMessageKey(chatKey);
      
      const messagesToStore = entry.items.map(msg => ({
        ...msg,
        content: {
          ...msg.content,
          text: msg.content?.text,
          recipients: msg.content?.recipients,
          listId: msg.content?.listId,
          recipientCount: msg.content?.recipientCount,
          presignUrl: undefined,
          isLoadingPresign: undefined,
          presignError: undefined,
          localUrl: msg.content?.localUrl?.startsWith?.('blob:') ? undefined : msg.content?.localUrl,
          mediaData: undefined
        },
        // Ensure essential message properties are preserved
        id: msg.id || msg.messageId || `msg_${Date.now()}`,
        messageId: msg.messageId || msg.id,
        timestamp: msg.timestamp || new Date().toISOString(),
        fromMe: Boolean(msg.fromMe),
        type: msg.type || 'broadcast',
        status: msg.status || 0,
        isBroadcast: true,
        isOptimistic: Boolean(msg.isOptimistic),
        jobId: msg.jobId
      }));
      
      const dataToStore = {
        messages: messagesToStore,
        lastUpdated: new Date().toISOString(),
        version: '1.1', // Increment version
        chatKey, // Store the chat key for validation
        messageCount: messagesToStore.length
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      
      if (entry) {
        entry.isLoaded = true;
      }
    } catch (error) {
      try {
        const entry = this.messagesByChatId.get(chatKey);
        if (entry?.items) {
          // Keep only essential data for the last 20 messages
          const minimal = entry.items.slice(-20).map(msg => ({
            id: msg.id || msg.messageId || `msg_${Date.now()}`,
            messageId: msg.messageId || msg.id,
            content: { 
              text: msg.content?.text || '',
              recipientCount: msg.content?.recipientCount || 0,
              listId: msg.content?.listId
            },
            timestamp: msg.timestamp || new Date().toISOString(),
            status: msg.status || 0,
            type: msg.type || 'broadcast',
            fromMe: Boolean(msg.fromMe),
            isBroadcast: true
          }));
          
          const minimalData = {
            messages: minimal,
            version: '1.1',
            lastUpdated: new Date().toISOString(),
            isMinimal: true
          };
          
          localStorage.setItem(
            this._generateBroadcastMessageKey(chatKey), 
            JSON.stringify(minimalData)
          );
        }
      } catch (retryError) {
        console.error('❌ Retry save also failed:', retryError);
      }
    }
  }

  _loadBroadcastMessagesFromStorage(chatKey) {
    try {
      const storageKey = this._generateBroadcastMessageKey(chatKey);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return [];
      
      const data = JSON.parse(stored);
      let messages = [];
      
      // Handle both old and new format
      if (Array.isArray(data)) {
        messages = data; // Old format
      } else if (data.messages && Array.isArray(data.messages)) {
        messages = data.messages; // New format
      } else {
        return [];
      }
    
      // Validate and normalize message structure
      const validMessages = messages.map(msg => {
        // Ensure essential properties exist
        return {
          ...msg,
          id: msg.id || msg.messageId || `msg_${Date.now()}`,
          messageId: msg.messageId || msg.id,
          timestamp: msg.timestamp || new Date().toISOString(),
          content: {
            text: '',
            recipientCount: 0,
            ...msg.content // Spread existing content after defaults
          },
          fromMe: Boolean(msg.fromMe),
          type: msg.type || 'broadcast',
          status: msg.status || 0,
          isBroadcast: true,
          isOptimistic: Boolean(msg.isOptimistic)
        };
      }).filter(msg => {
        // Filter out invalid messages
        const hasId = msg.id || msg.messageId;
        const hasTimestamp = msg.timestamp;
        const hasContent = msg.content;
        
        if (!hasId || !hasTimestamp || !hasContent) {
          return false;
        }
        
        return true;
      });
      
      return validMessages;
    } catch (error) {
      // Try to recover by removing corrupted data
      try {
        const storageKey = this._generateBroadcastMessageKey(chatKey);
        localStorage.removeItem(storageKey);
      } catch (removeError) {
        console.error('❌ Failed to remove corrupted data:', removeError);
      }
      
      return [];
    }
  }

  _removeBroadcastMessagesFromStorage(chatKey) {
    try {
      const storageKey = this._generateBroadcastMessageKey(chatKey);
      localStorage.removeItem(storageKey);
    } catch (error) {
      // console.error('Failed to remove broadcast messages:', error);
    }
  }

  // ===== Broadcast Utilities =====

  getBroadcastMessages(listId, propertyId, isSuperkos = false) {
    const key = this._generateBroadcastKey(listId, propertyId, isSuperkos);
    
    // Ensure messages are loaded before returning
    let entry = this.messagesByChatId.get(key);
    
    if (!entry || !entry.isLoaded) {
      // Auto-load if not loaded
      const chatId = `broadcast_${listId}`;
      this.loadBroadcastMessagesIfNeeded(chatId, propertyId, isSuperkos);
      entry = this.messagesByChatId.get(key);
    }
    
    return entry?.items ? entry.items.slice() : [];
  }

  isBroadcastChat(chatId) {
    return typeof chatId === 'string' && chatId.startsWith('broadcast_');
  }

  getBroadcastListById(propertyId, listId) {
    const lists = this.getBroadcastLists(propertyId);
    return lists.find(list => list.id === listId);
  }

  // Get broadcast stats
  getBroadcastStats(propertyId) {
    const lists = this.getBroadcastLists(propertyId);
    let totalMessages = 0;
    let totalRecipients = 0;

    lists.forEach(list => {
      const messages = this.getBroadcastMessages(list.id, propertyId);
      totalMessages += messages.length;
      
      messages.forEach(msg => {
        totalRecipients += msg.content?.recipientCount || 0;
      });
    });

    return {
      totalLists: lists.length,
      totalMessages,
      totalRecipients
    };
  }
  
  // ===== Desktop Notification =====

  _showDesktopNotification(msg) {
    if (!msg || msg.fromMe) return;
    
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const title = msg.pushName || msg.sender || 'New Message';
        let body = '';
        
        if (msg.content?.text) {
          body = msg.content.text;
        } else if (msg.type) {
          body = `Sent ${msg.type}`;
        } else {
          body = 'New message received';
        }
        
        const notification = new Notification(title, {
          body: body.substring(0, 100),
          icon: '/favicon.ico',
          tag: `whatsapp-${msg.propertyId}-${msg.key?.remoteJid}`,
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // Send message to open chat
          window.postMessage({
            __wa_open_chat: true,
            chatId: msg.key?.remoteJid,
            propertyId: msg.propertyId
          }, '*');
        };
        
        // Auto close after 5 seconds
        setTimeout(() => notification.close(), 5000);
      }
    } catch (error) {
      // console.warn('Notification failed:', error);
    }
  }

  
  // ===== Legacy Method Compatibility =====
  // Keep these for backward compatibility but mark as deprecated

  /**
   * @deprecated Use setBroadcastLists instead
   */
  saveBroadcastListsToStorage(propertyId, lists) {
    this.setBroadcastLists(propertyId, lists);
  }

  /**
   * @deprecated Use getBroadcastLists instead
   */
  loadBroadcastListsFromStorage(propertyId) {
    return this.getBroadcastLists(propertyId).slice();
  }

  /**
   * @deprecated Use sendBroadcastMessage instead
   */
  sendMessagesBroadcast(propertyId, payload) {
    return this.sendBroadcastMessage(propertyId, payload);
  }

  /**
   * @deprecated Use _createOptimisticBroadcastMessage instead
   */
  storeBroadcastMessage(listId, propertyId, message, isSuperkos = false) {
    this._createOptimisticBroadcastMessage(listId, propertyId, {
      tempId: `legacy_${Date.now()}`,
      text: message.text,
      phones: message.phones || []
    });
  }
}